import { resourcesApi } from '../services/resources-api';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';

// Local type for MindMap response
interface MindMapResponse {
  topic: string;
  markdown: string;
  sources: Array<{ id: string; title: string }>;
}

export type OnSourceSelectCallback = (docId: string, source: string) => void;

export class MindMapView {
  private panelEl: HTMLElement;
  private contentEl: HTMLElement;
  private svgEl: SVGSVGElement | null = null;
  private markmap: Markmap | null = null;
  private transformer: Transformer;
  private isOpen: boolean = false;
  private isLoading: boolean = false;
  private currentResponse: MindMapResponse | null = null;
  private onSourceSelect: OnSourceSelectCallback | null = null;

  constructor() {
    this.transformer = new Transformer();
    this.createPanel();
    this.panelEl = document.getElementById('mindmap-panel')!;
    this.contentEl = document.getElementById('mindmap-content')!;
    this.setupEventListeners();
  }

  private createPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'mindmap-panel';
    panel.className = 'mindmap-panel hidden';
    panel.innerHTML = `
      <div class="mindmap-container">
        <div class="mindmap-header">
          <h3>Mind Map</h3>
          <div class="mindmap-actions">
            <button id="mindmap-export-btn" class="mindmap-btn" title="Export Markdown">
              Export
            </button>
            <button id="mindmap-close-btn" class="mindmap-btn" title="Close">
              &times;
            </button>
          </div>
        </div>
        <div id="mindmap-content" class="mindmap-content">
          <div class="mindmap-hint">
            Enter a topic to generate a mind map from your knowledge base.
          </div>
        </div>
        <div class="mindmap-footer">
          <div id="mindmap-sources" class="mindmap-sources"></div>
        </div>
      </div>
    `;
    document.body.appendChild(panel);
  }

  private setupEventListeners(): void {
    // Close button
    document.getElementById('mindmap-close-btn')?.addEventListener('click', () => {
      this.close();
    });

    // Export button
    document.getElementById('mindmap-export-btn')?.addEventListener('click', () => {
      this.exportMarkdown();
    });

    // Click outside to close
    this.panelEl.addEventListener('click', (e) => {
      if (e.target === this.panelEl) {
        this.close();
      }
    });

    // Keyboard shortcut: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setOnSourceSelect(callback: OnSourceSelectCallback): void {
    this.onSourceSelect = callback;
  }

  open(topic?: string): void {
    this.isOpen = true;
    this.panelEl.classList.remove('hidden');

    if (topic) {
      this.generateMindMap(topic);
    }
  }

  close(): void {
    this.isOpen = false;
    this.panelEl.classList.add('hidden');

    // Cleanup markmap
    if (this.markmap) {
      this.markmap = null;
    }
    if (this.svgEl) {
      this.svgEl.remove();
      this.svgEl = null;
    }
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  async generateMindMap(topic: string): Promise<void> {
    if (!topic || topic.length < 2) return;
    if (this.isLoading) return;

    this.isLoading = true;
    this.renderLoading(topic);

    try {
      const response = await resourcesApi.mindmap(topic, 8);

      if (response.success && response.data) {
        this.currentResponse = response.data;
        this.renderMindMap();
        this.renderSources();
      } else {
        this.renderError(response.error || 'Failed to generate mind map');
      }
    } catch (error: any) {
      this.renderError(error.message || 'Network error');
    } finally {
      this.isLoading = false;
    }
  }

  private renderLoading(topic: string): void {
    this.contentEl.innerHTML = `
      <div class="mindmap-loading">
        <div class="mindmap-spinner"></div>
        <p>Generating mind map for "${this.escapeHtml(topic)}"...</p>
      </div>
    `;
    this.clearSources();
  }

  private renderError(message: string): void {
    this.contentEl.innerHTML = `
      <div class="mindmap-error">
        <p>Error: ${this.escapeHtml(message)}</p>
        <button class="mindmap-retry-btn">Try Again</button>
      </div>
    `;
    this.clearSources();
  }

  private renderMindMap(): void {
    if (!this.currentResponse) return;

    // Clear previous content
    this.contentEl.innerHTML = '';

    // Create SVG element
    this.svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svgEl.id = 'mindmap-svg';
    this.svgEl.style.width = '100%';
    this.svgEl.style.height = '100%';
    this.contentEl.appendChild(this.svgEl);

    // Transform markdown to markmap data
    const { root } = this.transformer.transform(this.currentResponse.markdown);

    // Create markmap
    this.markmap = Markmap.create(this.svgEl, {
      autoFit: true,
      duration: 500,
      maxWidth: 300,
      color: (node: any) => {
        // Color based on depth
        const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'];
        return colors[node.state?.depth % colors.length] || colors[0];
      }
    }, root);
  }

  private renderSources(): void {
    if (!this.currentResponse || this.currentResponse.sources.length === 0) {
      this.clearSources();
      return;
    }

    const sourcesEl = document.getElementById('mindmap-sources')!;
    sourcesEl.innerHTML = `
      <span class="mindmap-sources-label">Sources:</span>
      ${this.currentResponse.sources.map(source => `
        <span class="mindmap-source-tag" data-doc-id="${source.id}" data-source="resource">
          &#128196; ${this.escapeHtml(source.title)}
        </span>
      `).join('')}
    `;

    // Add click handlers
    sourcesEl.querySelectorAll('.mindmap-source-tag').forEach(el => {
      el.addEventListener('click', () => {
        const docId = el.getAttribute('data-doc-id');
        const source = el.getAttribute('data-source');

        if (docId && source && this.onSourceSelect) {
          this.onSourceSelect(docId, source);
          this.close();
        }
      });
    });
  }

  private clearSources(): void {
    const sourcesEl = document.getElementById('mindmap-sources');
    if (sourcesEl) {
      sourcesEl.innerHTML = '';
    }
  }

  private exportMarkdown(): void {
    if (!this.currentResponse) return;

    const markdown = this.currentResponse.markdown;
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mindmap-${this.currentResponse.topic.replace(/\s+/g, '-').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
